/**
 * GNS.IndexScene
 * * トップページ（/#index）のメイン制御クラス
 * * 機能:
 * - メインビジュアル（背景Vimeo動画）の再生とリサイズ制御
 * - ショーリール（オーバーレイ動画）の開閉制御
 * - ニュース・実績サムネイルの生成
 * - スクロールに応じたポップアップ表示制御
 */
GNS.IndexScene = function (GNS) {
    // AbstractSceneを継承
    const scene = GNS.create(GNS.AbstractScene);

    /**
     * 初期化処理
     */
    scene.init = function (data) {
        scene._super.init.call(this, data);

        // Showreel IDの初期設定（HTML属性から取得）
        this._initShowreelConfig();

        // UI要素の初期化
        this.ui = {
            top: new GNS.DisplayObject("index__top"),
            bg: new GNS.DisplayObject("index__top__bg"),
            showreelBtn: new GNS.DisplayObject("index__top__ui__showreel"),
            arrow: new GNS.DisplayObject("index__top__ui__arror"),
            menuBtn: new GNS.DisplayObject("index__top__ui__menu__inner"),
            caseTitle: new GNS.DisplayObject("index__top__ui__case_title"),

            // ショーリールオーバーレイ用
            overlay: new GNS.DisplayObject("index__top__showreel"),
            vimeoContainer: new GNS.DisplayObject("index__top__showreel__contener"),
            closeBtn: new GNS.DisplayObject("index__top__showreel__contener__close")
        };

        // 状態管理
        this.buttons = []; // UIThumBtnインスタンス
        this.bgVideoObj = null; // 背景動画のDisplayObject
        this.overlayPlayer = null; // ショーリール用Vimeo Player

        // イベント登録
        this.ui.showreelBtn.addEvent("click", this._onShowreelOpen);
        this.ui.closeBtn.addEvent("click", this._onShowreelClose);
        this.ui.menuBtn.addEvent("click", this._onMenuClick);

        // ヘッダーイベント連携
        const header = GNS.Header.getInstance();
        header.addEventListener("nav_open", this._onMenuOpened);
        header.addEventListener("nav_close", this._onMenuClosed);

        // 初期配置調整
        // 本番サイトと同じく、vimeoContainerのy位置のみ設定
        // overlayの初期化は行わない（CSSのデフォルトに任せる）
        TweenMax.set(this.ui.vimeoContainer.element, {
            y: -20
        });

        // スクロールイベント監視（バインド済み関数を保持してremove可能にする）
        this._boundScrollHandler = this.scrollHandler.bind(this);
        if (GNS.SceneController.scrollPopup) {
            GNS.DOM.addEvent(document, "scroll", this._boundScrollHandler);
            this.scrollHandler(); // 初回チェック
        }

        // リサイズ監視
        GNS.ResizeManager.addEventListener(this.resize);
        this.resize();
    };

    /**
     * data属性からShowreel設定を読み込むヘルパー
     */
    scene._initShowreelConfig = function () {
        try {
            const showreelId = this.element && this.element.getAttribute ?
                this.element.getAttribute("data-showreel") :
                "";

            window.SHOWREEL_CONFIG = window.SHOWREEL_CONFIG || {};
            if (showreelId) {
                window.SHOWREEL_CONFIG.showreel = showreelId;
            }
        } catch (ex) {
            window.SHOWREEL_CONFIG = {};
        }
    };

    /**
     * リサイズ処理
     */
    scene._bind.resize = function () {
        const pageSize = GNS.DOM.getPageSize();

        // トップエリアの高さを画面高さいっぱいに
        this.ui.top.element.style.height = `${pageSize.displayHeight}px`;

        // 背景動画のリサイズ（Cover処理）
        if (this.bgVideoObj) {
            this._fitVideoToCover(this.bgVideoObj, true);
        }
    };

    /**
     * ロード処理
     * Showreel IDはPHPが出力した window.SHOWREEL_CONFIG または data属性から取得済み
     */
    scene.load = function () {
        // API経由でなく直接設定を確認
        const vimeoId = (window.SHOWREEL_CONFIG && window.SHOWREEL_CONFIG.showreel) ? window.SHOWREEL_CONFIG.showreel : null;

        if (vimeoId) {
            // 1. オーバーレイ用プレーヤー（音声あり、操作可能）
            this._setupOverlayPlayer(vimeoId);

            // 2. 背景用プレーヤー（ミュート、ループ、操作不可）
            this._setupBackgroundPlayer(vimeoId);
        } else {
            // 動画IDがない場合はコンテンツロードへ進む
            this._loadContent();
        }
    };

    /**
     * オーバーレイ用Vimeoプレーヤーのセットアップ
     */
    scene._setupOverlayPlayer = function (vimeoId) {
        // プレーヤー初期化
        this.overlayPlayer = new window.Vimeo.Player(this.ui.vimeoContainer.element, {
            id: vimeoId,
            loop: true,
            autoplay: false,
            byline: false,
            portrait: false,
            playsinline: false, // 全画面になりにくい設定
            title: false,
            controls: true
        });

        // SPの場合、タップで再生/一時停止できる透明な壁（Wall）を作成
        if (GNS.isSP) {
            const wall = new GNS.DisplayObject(document.createElement("div"));
            wall.addClass("wall");
            this.ui.vimeoContainer.element.appendChild(wall.element);

            wall.addEvent("click", () => {
                this.overlayPlayer.getPaused().then((paused) => {
                    paused ? this.overlayPlayer.play().catch(() => { }) :
                        this.overlayPlayer.pause().catch(() => { });
                });
            });
            this.overlayPlayerWall = wall;
        }
    };

    /**
     * 背景用Vimeoプレーヤーのセットアップ
     */
    scene._setupBackgroundPlayer = function (vimeoId) {
        // HTML構造生成
        let html = '<div class="top_bg_color_black"></div>';
        html += '<div id="video_loading" class="index__top__loader"><div class="page_loading__spinner"></div></div>';
        html += '<div id="top_vimeo0" class="top__img__vimeo"></div>';
        this.ui.bg.element.innerHTML = html;

        const bgVideoEl = new GNS.DisplayObject("top_vimeo0");
        const videoLoadingEl = new GNS.DisplayObject("video_loading");

        // プレーヤー初期化
        const player = new window.Vimeo.Player(bgVideoEl.element, {
            id: vimeoId,
            loop: true,
            autoplay: true,
            muted: true, // 自動再生にはミュート必須
            background: true, // 背景モード（UIなし）
            playsinline: true
        });

        player.setVolume(0);

        // プロパティ保持
        this.bgVideoObj = bgVideoEl;
        this.bgVideoObj.player = player;
        this.bgVideoObj.addClass("active");

        // 初期リサイズ
        this._fitVideoToCover(this.bgVideoObj, true);

        // ロード完了したらフェードインして、コンテンツロードへ
        player.on("loaded", () => {
            TweenMax.to(this.bgVideoObj.element, 0.5, {
                autoAlpha: 1
            });
            // ローダーをフェードアウトして非表示
            if (videoLoadingEl && videoLoadingEl.element) {
                TweenMax.to(videoLoadingEl.element, 0.5, {
                    delay: 1.0, // そのままフェードアウトすると速すぎる場合があるので、最低1秒は見せる
                    autoAlpha: 0,
                    onComplete: function () {
                        videoLoadingEl.element.style.display = 'none';
                    }
                });
            }
            this._loadContent();
        });
    };

    /**
     * コンテンツデータのロード（APIまたはDOM）
     */
    scene._loadContent = function () {
        // PHPによるSSRでコンテンツは出力済みなので、APIリクエストは不要
        // 即座にセットアップへ移行
        this._onContentLoaded(null);
    };

    /**
     * API/DOMロード完了後のUI生成処理
     */
    scene._bind._onContentLoaded = function () {
        // GNS.APITop.dispatcher.removeEventListener(GNS.Event.COMPLETE, this._onContentLoaded);

        // 1. ニュースサムネイルの初期化 (ID: 0~2)
        this._initNewsButtons();

        // 2. 実績サムネイルの初期化 (ID: 0~5)
        this._initCasesButtons();

        // 全ロード完了
        scene._super.load.call(this);
    };

    /**
     * ニュースボタンの初期化
     */
    scene._initNewsButtons = function () {
        for (let i = 0; i < 3; i++) {
            const imgObj = new GNS.DisplayObject(`news__container__line__box__column__img${i}`);
            const boxObj = new GNS.DisplayObject(`news__container__line__box${i}`);
            const spLinkObj = new GNS.DisplayObject(`item__new__link__sp${i}`);

            // UIThumBtn(TriggerID, BG, Image, Box, SPLink, Delay)
            const btn = new GNS.UIThumBtn(`item__new__link__pc${i}`, null, imgObj, boxObj, spLinkObj, 0.05 * i);

            if (btn.element) {
                this.buttons.push(btn);
            }
        }
    };

    /**
     * 実績ボタン（Cases）の初期化
     * ホバー時のVimeo再生機能付き
     */
    scene._initCasesButtons = function () {
        for (let i = 0; i < 6; i++) {
            const bgObj = new GNS.DisplayObject(`index__cases__item__bg${i}`);

            let vimeoPlayer = null;
            let videoContainer = null;

            // DOMからVimeo IDとコンテナを探す
            if (bgObj.element) {
                // コンテナ検索
                const children = bgObj.element.childNodes;
                for (let j = 0; j < children.length; j++) {
                    if (children[j].className && children[j].className.indexOf("index__cases__item__bg__image") !== -1) {
                        videoContainer = children[j];
                        break;
                    }
                }

                // ID検索 (クラス名 "movie--12345" から抽出)
                const match = bgObj.element.className.match(/movie--(\d+)/);
                if (match && match[1] && videoContainer) {
                    const vimeoId = match[1];

                    // ホバー用プレーヤー作成
                    vimeoPlayer = new window.Vimeo.Player(videoContainer, {
                        id: vimeoId,
                        loop: true,
                        autoplay: false,
                        background: true, // UIなし
                        muted: true
                    });

                    // エラーハンドリング追加 (無効なIDの場合のコンソールエラー抑制)
                    vimeoPlayer.ready().catch(function (error) {
                        console.warn("Vimeo init failed for ID " + vimeoId, error);
                    });
                    vimeoPlayer.on('error', function (data) {
                        console.warn("Vimeo error:", data);
                    });

                    vimeoPlayer.setVolume(0).catch(function () { });
                }
            }

            const imgObj = new GNS.DisplayObject(`index__cases__item__bg__image${i}`); // 画像要素

            // UIThumBtn(TriggerID, Player, Image, Box(null), SPLink(null), Delay)
            const btn = new GNS.UIThumBtn(`index__cases__item${i}`, vimeoPlayer, imgObj, null, null, 0.05 * i);

            if (btn.element) {
                this.buttons.push(btn);
            }
        }
    };

    /**
     * 表示ハンドラ
     */
    scene.showHandler = function () {
        this.resize();

        // メニューテキストを遅延して隠す演出
        this.closeMenuTimer = TweenMax.delayedCall(3, () => this.ui.menuBtn.addClass("closemenu"));

        // スクロールアイコンのアニメーション開始
        this._animateScrollIcon();
    };

    /**
     * スクロールアイコンのアニメーションループ
     */
    scene._bind._animateScrollIcon = function () {
        // 1. 下にフェードアウト
        this.arrowTween1 = TweenMax.to(this.ui.arrow.element, 0.3, {
            y: 0,
            opacity: 0,
            delay: 0.6,
            ease: Cubic.easeOut
        });

        // 2. 上に瞬時に戻る（見えない状態で）
        this.arrowTween2 = TweenMax.to(this.ui.arrow.element, 0, {
            y: -9,
            delay: 1.3,
            ease: Linear.easeNone
        });

        // 3. 上からフェードインして戻る -> ループ
        this.arrowTween3 = TweenMax.to(this.ui.arrow.element, 0.3, {
            y: 0,
            opacity: 1,
            delay: 1.5,
            ease: Cubic.easeOut,
            onComplete: this._animateScrollIcon // 再帰呼び出し
        });
    };

    /**
     * 非表示ハンドラ
     */
    scene.hideHandler = function () {
        // イベント解除
        this.buttons.forEach(btn => {
            if (btn.kill) btn.kill();
        });
        this.buttons = [];

        // 状態リセット
        this.ui.menuBtn.removeClass("closemenu");
        this.ui.showreelBtn.removeClass("closemenu");

        // アニメーション停止
        if (this.closeMenuTimer) TweenMax.killTweensOf(this.closeMenuTimer);
        if (this.arrowTween1) TweenMax.killTweensOf(this.arrowTween1.element);
        if (this.arrowTween2) TweenMax.killTweensOf(this.arrowTween2.element);
        if (this.arrowTween3) TweenMax.killTweensOf(this.arrowTween3.element);

        // 背景動画イベント解除
        if (this.bgVideoObj && this.bgVideoObj.player) {
            // 必要であればoff処理 (Vimeo API依存)
        }
    };

    /**
     * リセットハンドラ
     * (シーン破棄時など完全にクリーンアップする場合)
     */
    scene.resetHandler = function () {
        GNS.Header.getInstance().removeEventListener("nav_open", this._onMenuOpened);
        GNS.Header.getInstance().removeEventListener("nav_close", this._onMenuClosed);
        GNS.ResizeManager.removeEventListener(this.resize);

        if (this._boundScrollHandler) {
            GNS.DOM.removeEvent(document, "scroll", this._boundScrollHandler);
        }
    };

    // ----------------------------------------------------------------
    // Interaction Handlers
    // ----------------------------------------------------------------

    /**
     * Showreelボタンクリック：オーバーレイ表示
     */
    scene._bind._onShowreelOpen = function () {
        GNS.Wrapper.getInstance().saveScroll();
        // setFixed(true)の代わりにbodyのoverflowを直接制御
        // これによりスクロール位置がリセットされない
        document.body.style.overflow = "hidden";

        // 初期位置セット
        TweenMax.set(this.ui.vimeoContainer.element, {
            autoAlpha: 0,
            y: -40
        });

        // オーバーレイコンテナ表示
        TweenMax.to(this.ui.vimeoContainer.element, 0.6, {
            autoAlpha: 1,
            y: 0,
            ease: Cubic.easeOut,
            delay: 0.2
        });

        // 黒背景フェードイン
        // スクロール位置を取得して、オーバーレイのtopを設定
        const scrollY = GNS.DOM.getPageSize().scrollY;
        this.ui.overlay.element.style.setProperty("top", `${scrollY}px`, "important");
        TweenMax.to(this.ui.overlay.element, 0.35, {
            delay: 0,
            autoAlpha: 1,
            pointerEvents: "auto",
            ease: Power2.easeIn
        });

        // 背景動画は一時停止
        if (this.bgVideoObj && this.bgVideoObj.player) {
            this.bgVideoObj.player.pause().catch(() => { });
        }
    };

    /**
     * Closeボタンクリック：オーバーレイ非表示
     */
    scene._bind._onShowreelClose = function () {
        // 背景動画再開
        if (this.bgVideoObj && this.bgVideoObj.player) {
            this.bgVideoObj.player.play().catch(() => { });
        }

        // オーバーレイ動画停止 & リセット
        if (this.overlayPlayer) {
            this.overlayPlayer.pause().catch(() => { });
            this.overlayPlayer.setCurrentTime(0);
        }

        GNS.Wrapper.getInstance().restoreScroll();
        // setFixed(false)の代わりにbodyのoverflowを復元
        document.body.style.overflow = "";

        // フェードアウトアニメーション
        TweenMax.to(this.ui.vimeoContainer.element, 0.5, {
            autoAlpha: 0,
            y: 30,
            ease: Cubic.easeOut
        });

        TweenMax.to(this.ui.overlay.element, 0.5, {
            autoAlpha: 0,
            pointerEvents: "none",
            ease: Cubic.easeOut
        });
    };

    scene._bind._onMenuOpened = function () {
        this.ui.menuBtn.addClass("on");
    };

    scene._bind._onMenuClosed = function () {
        this.ui.menuBtn.removeClass("on");
    };

    scene._bind._onMenuClick = function () {
        GNS.Header.getInstance().toggleMenu(true);
    };

    // ----------------------------------------------------------------
    // Helper: Video Resize (Aspect Ratio Fit)
    // ----------------------------------------------------------------

    /**
     * 動画/画像をコンテナに合わせてCoverサイズに調整する（CSS object-fit: cover 相当）
     * @param {DisplayObject} targetObj - 対象の動画/画像要素
     * @param {boolean} isCover - trueならcover, falseならcontain (元のコードではcoverのみ使用)
     */
    scene._bind._fitVideoToCover = function (targetObj, isCover) {
        const pageSize = GNS.DOM.getPageSize();
        const stageW = pageSize.stageWidth; // 画面幅
        const stageH = pageSize.displayHeight; // 画面高さ

        // ターゲットの元サイズ（未指定なら16:9と仮定）
        const rect = GNS.DOM.getRect(targetObj.element);
        const targetW = rect.width || 16;
        const targetH = rect.height || 9;

        const targetRatio = stageW / stageH;
        const sourceRatio = targetW / targetH;
        const standardRatio = 16 / 9; // ビデオの標準アスペクト比

        let finalW = 0;
        let finalH = 0;

        // 画面アスペクト比とソース（動画）のアスペクト比を比較
        // ここでは16:9を基準に計算している
        if (targetRatio > sourceRatio) {
            // 画面の方が横長 -> 幅に合わせて高さをクロップ（または拡大）
            finalW = stageW;
            finalH = stageW / standardRatio;
        } else {
            // 画面の方が縦長 -> 高さに合わせて幅をクロップ
            finalW = stageH * standardRatio;
            finalH = stageH;
        }

        // CSS適用
        targetObj.element.style.width = `${finalW}px`;
        targetObj.element.style.height = `${finalH}px`;

        // 中央寄せ
        const offsetX = (stageW - finalW) / 2;
        const offsetY = (stageH - finalH) / 2;

        targetObj.element.style.left = `${offsetX}px`;
        targetObj.element.style.top = `${offsetY}px`;
    };

    /**
     * スクロール量に応じたポップアップ表示制御
     */
    scene.scrollHandler = function () {
        const pageSize = GNS.DOM.getPageSize();
        const scrollY = pageSize.scrollY;
        const viewH = pageSize.displayHeight;
        const totalH = pageSize.stageHeight;

        // スクロール進捗率 (0.0 ~ 1.0)
        const progress = scrollY / (totalH - viewH);

        // 1画面分以上スクロールし、かつフッター手前(94%)の場合に表示
        if (scrollY >= 0.25 * viewH && progress <= 0.94) {
            GNS.SceneController.scrollPopup.addClass("show");
        } else {
            GNS.SceneController.scrollPopup.removeClass("show");
        }
    };

    return scene._self;
}(GNS);
